// src/features/analytics/components/EventTotalsCard.tsx
//
// Event totals (dashboard ADR-009): the playback/window response — sessions,
// unique viewers, watch time over the hour-aligned EFFECTIVE window (echoed
// by the gateway and shown here so the hour snap is never a surprise), plus
// the minute-exact peak and its time. Honest empty: a window with no viewers
// says so in words and offers ONE way forward (the latest broadcast, or
// yesterday when the window is today) — never blank tiles; loading stays a
// skeleton, distinct from empty.

import { Button, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import type { DayZone } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import {
  emptyTotalsAction,
  emptyTotalsTitle,
  instantLabel,
  isTodayWindow,
  snapWindowToHours,
  windowLabel,
  type EventWindow,
} from '../eventWindow';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import { StatTile } from './StatTile';
import type { AnalyticsEnvelope, BroadcastRow, PlaybackWindowData } from '../types';

interface EventTotalsCardProps {
  requested: EventWindow;
  isCustom: boolean;
  /** Card title; defaults to "Event totals" for an explicit window and "Range totals" otherwise. */
  title?: string;
  data: AnalyticsEnvelope<PlaybackWindowData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
  zone: DayZone;
  /** Hour-stable "now" (the end of the current hour). */
  nowSeconds: number;
  /** Shaped broadcasts (newest first) — the empty state's first choice of action. */
  broadcasts: readonly BroadcastRow[];
  /** Apply the empty state's action (writes the window to the URL). */
  onPickWindow: (window: EventWindow) => void;
}

export function EventTotalsCard({
  requested,
  isCustom,
  title,
  data,
  isLoading,
  isError,
  error,
  onRetry,
  zone,
  nowSeconds,
  broadcasts,
  onPickWindow,
}: EventTotalsCardProps) {
  const effective = data
    ? { from: data.data.effectiveFrom, to: data.data.effectiveTo }
    : snapWindowToHours(requested);
  const snapped = effective.from !== requested.from || effective.to !== requested.to;
  const totals = data?.data;
  const hasTotals =
    !!totals &&
    (totals.sessions > 0 ||
      totals.watchTimeMs > 0 ||
      totals.uniqueViewers > 0 ||
      (totals.peakConcurrent !== undefined && totals.peakConcurrent > 0));
  const isToday = isTodayWindow(requested, nowSeconds, zone);
  const action = emptyTotalsAction(requested, nowSeconds, zone, broadcasts);
  // The gateway answers 422 for a window after the last data hour; that is "no viewers yet", not an error.
  const noDataYet = isError && AnalyticsUtils.isNoDataYetError(error, requested);
  const showEmpty = noDataYet || (!isLoading && !!data && !hasTotals);

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" align="baseline" mb="xs" wrap="wrap">
        <Title order={5}>{title ?? (isCustom ? 'Event totals' : 'Range totals')}</Title>
        <Text size="xs" c="dimmed">
          {windowLabel(effective, zone)}
          {snapped && ` (whole hours; you asked for ${windowLabel(requested, zone)})`}
        </Text>
      </Group>
      {isError && !noDataYet ? (
        <AnalyticsErrorState error={error} onRetry={onRetry} />
      ) : showEmpty ? (
        <Stack gap="xs" py="sm">
          {data && <AnalyticsCoverageNotice coverage={data.coverage} freshness={data.freshness} range={effective} zone={zone} />}
          <Text size="sm" fw={500}>
            {emptyTotalsTitle(isToday)}
          </Text>
          <Text size="sm" c="dimmed">
            Nobody watched this stream in the window. Sessions, viewers, watch time, and peak are all zero.
          </Text>
          {action && (
            <Group>
              <Button variant="light" size="xs" onClick={() => onPickWindow(action.window)}>
                {action.label}
              </Button>
            </Group>
          )}
        </Stack>
      ) : (
        <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="sm">
          <StatTile label="Sessions" value={totals ? AnalyticsUtils.formatCount(totals.sessions) : null} isLoading={isLoading} />
          <StatTile label="Unique viewers" value={totals ? AnalyticsUtils.formatCount(totals.uniqueViewers) : null} isLoading={isLoading} />
          <StatTile label="Watch time" value={totals ? AnalyticsUtils.formatWatchTime(totals.watchTimeMs) : null} isLoading={isLoading} />
          <StatTile
            label="Peak concurrent"
            value={totals?.peakConcurrent !== undefined ? AnalyticsUtils.formatCount(totals.peakConcurrent) : null}
            emptyCopy="No viewers at once"
            secondary={
              totals?.peakAt !== undefined ? (
                <Text size="xs" c="dimmed" mt={2}>
                  at {instantLabel(totals.peakAt, zone)}
                </Text>
              ) : undefined
            }
            isLoading={isLoading}
          />
        </SimpleGrid>
      )}
      <Text size="xs" c="dimmed" mt="xs">
        Totals are counted over whole hours; the peak is exact to the minute. Times are in your
        local timezone.
      </Text>
    </Paper>
  );
}
